import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { getBackendUrl } from '../config/api';
import { useAuth } from './AuthContext';

const UserModeContext = createContext(null);

const SEEKER_DEFAULTS = {
  mode: 'seeker',
  isAstrologerMode: false,
  isSeekerMode: true,
  nav: {
    showMatch: true, showWorkstation: false,
    showClientManager: false, showKPTables: false, showHoroscope: true,
  },
  dashboard: {
    showCosmicSnapshot: true, showMuhurat: true,
    showCosmicInsight: true, showExploreDestiny: true,
    showClientQuickAccess: false, showRecentCharts: false,
  },
  setMode: () => {},
  toggleMode: () => {},
  chitraguptMode: 'conversational',
};

/** The mode an account explicitly chose, or null when it never chose one. */
export const accountMode = (user) => {
  if (!user || !user.astrologer_mode_set_at) return null;
  return user.astrologer_mode ? 'astrologer' : 'seeker';
};

export const UserModeProvider = ({ children }) => {
  // Mode is the user's explicit choice. It starts from localStorage
  // (`jp_user_mode`) so the first paint needs no network, then follows the
  // account once the signed-in user loads (see the effect below).
  //
  // The bare DB flag `user.astrologer_mode` is still NOT trusted on its own —
  // many accounts carry a stale `true` from past bugs, which used to flip
  // seekers into astrologer mode on login. The account is followed only when
  // it carries `astrologer_mode_set_at`, which PATCH /api/auth/settings stamps
  // when the user picks a mode (WB-08). Accounts without it keep today's
  // behaviour: localStorage decides, default `seeker`.
  const [mode, setModeState] = useState(() => {
    try {
      const stored = localStorage.getItem('jp_user_mode');
      if (stored === 'astrologer') return 'astrologer';
    } catch {}
    return 'seeker';
  });

  // Follow the account's explicit choice, so the mode set on one device is the
  // mode on the next. Keyed on the stamp: a later choice elsewhere re-syncs.
  const auth = useAuth();
  const accountChoice = accountMode(auth?.user);
  const accountStamp = auth?.user?.astrologer_mode_set_at || null;
  useEffect(() => {
    if (!accountChoice) return;
    setModeState(accountChoice);
    try { localStorage.setItem('jp_user_mode', accountChoice); } catch {}
  }, [accountChoice, accountStamp]);

  // One-time carry-over. Before the stamp existed, choosing Astrologer on this
  // device was recorded only in localStorage. ChitraGupt's voice now follows
  // the stamp, so an account that is in astrologer mode here but has no stamp
  // yet is stamped once, from that explicit local choice. A stale DB flag is
  // not enough: this runs only when the device itself says astrologer.
  const stampTriedRef = useRef(false);
  const signedInUser = auth?.user || null;
  useEffect(() => {
    if (stampTriedRef.current || !signedInUser || accountStamp || mode !== 'astrologer') return;
    let token = null;
    try { token = localStorage.getItem('jyotish_session_token'); } catch {}
    if (!token) return;
    stampTriedRef.current = true;
    fetch(`${getBackendUrl()}/api/auth/settings`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ astrologer_mode: true }),
    })
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (data?.applied && auth?.setUser) auth.setUser({ ...signedInUser, ...data.applied });
      })
      .catch(() => {});
  }, [signedInUser, accountStamp, mode]); // eslint-disable-line react-hooks/exhaustive-deps

  const isAstrologerMode = mode === 'astrologer';
  const isSeekerMode = !isAstrologerMode;

  const setMode = (val) => {
    setModeState(val);
    try { localStorage.setItem('jp_user_mode', val); } catch {}
  };

  const toggleMode = () => setMode(isAstrologerMode ? 'seeker' : 'astrologer');

  const nav = {
    showMatch:         isSeekerMode,
    showWorkstation:   isAstrologerMode,
    showClientManager: isAstrologerMode,
    showKPTables:      isAstrologerMode,
    showHoroscope:     isSeekerMode,
  };

  const dashboard = {
    showCosmicSnapshot:    isSeekerMode,
    showMuhurat:           isSeekerMode,
    showCosmicInsight:     isSeekerMode,
    showExploreDestiny:    isSeekerMode,
    showClientQuickAccess: isAstrologerMode,
    showRecentCharts:      isAstrologerMode,
  };

  return (
    <UserModeContext.Provider value={{
      mode, isAstrologerMode, isSeekerMode,
      nav, dashboard, setMode, toggleMode,
      chitraguptMode: isSeekerMode ? 'conversational' : 'technical',
    }}>
      {children}
    </UserModeContext.Provider>
  );
};

export const useUserMode = () => {
  const ctx = useContext(UserModeContext);
  if (!ctx) return SEEKER_DEFAULTS;
  // Defensive: ensure `dashboard` and `nav` are never null. If a Provider
  // ever passes an incomplete value (e.g. during SSR hydration or a
  // mid-flight mode transition), we merge in the seeker defaults so
  // downstream consumers like `dashboard.showCosmicSnapshot` never crash.
  return {
    ...SEEKER_DEFAULTS,
    ...ctx,
    dashboard: { ...SEEKER_DEFAULTS.dashboard, ...(ctx.dashboard || {}) },
    nav: { ...SEEKER_DEFAULTS.nav, ...(ctx.nav || {}) },
  };
};
